import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { withStyles, Theme, WithStyles } from '@lifeomic/chroma-react/styles';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Snackbar } from '@lifeomic/chroma-react/components/Snackbar';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { ClearList, X } from '@lifeomic/chromicons';
import _ from 'lodash';
import {
  notificationsActions,
  notificationsSelectors,
} from '@lifeomic/phc-web-toolkit/src/redux/modules/notifications';
import { Notification } from '@lifeomic/phc-web-toolkit/src/redux/modules/notifications/reducer';
import { WrappedComponentProps, defineMessages, injectIntl } from 'react-intl';
import { RootState } from '@lifeomic/phc-web-toolkit/src/redux/rootReducer';

const messages = defineMessages({
  dismiss: {
    id: 'appNotifications.dismiss',
    defaultMessage: 'Dismiss',
  },
  dismissAll: {
    id: 'appNotifications.dismissAll',
    defaultMessage: 'Dismiss all',
  },
});

const styles = (theme: Theme) => ({
  snackbarOverrides: {
    top: theme.spacing(7) + 2,
    right: theme.spacing(2),
    '@media screen and (max-width: 480px)': {
      top: 'unset',
      right: 'unset',
    },
  },
  title: {
    color: theme.palette.black[600],
  },
  divider: {
    margin: 0,
    marginLeft: theme.spacing(2),
    height: 18,
  },
});

function delayToBuffer(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

interface AppNotificationsStateProps {
  notifications: Notification[];
}

interface AppNotificationsState {
  currentNotification: Notification | null;
  currentNotificationIsOpen: boolean;
}

export type AppNotificationsDispatchProps = {
  removeNotification: () => void;
  purgeNotifications: () => void;
};

type AppNotificationsProps = AppNotificationsStateProps &
  AppNotificationsDispatchProps &
  WithStyles<typeof styles> &
  WrappedComponentProps;

class AppNotifications extends PureComponent<
  AppNotificationsProps,
  AppNotificationsState
> {
  state = {
    currentNotification: null,
    currentNotificationIsOpen: false,
  };

  handleSnackbarClose = () => {
    // Pop off the current notification from the queue
    // and reset local state
    this.props.removeNotification();
    this.setState({
      currentNotification: null,
      currentNotificationIsOpen: false,
    });
  };

  handlePurgeNotifications = () => {
    // Remove all notifications from the queue
    this.props.purgeNotifications();
    this.setState({
      currentNotification: null,
      currentNotificationIsOpen: false,
    });
  };

  async componentDidUpdate(
    _prevProps: AppNotificationsProps,
    prevState: AppNotificationsState,
  ) {
    const { notifications } = this.props;
    const { currentNotification, currentNotificationIsOpen } = this.state;

    // We have a notification currently displayed, so wait until it closes.
    if (currentNotificationIsOpen) {
      return;
    }

    // We have notifications, so let's display the first one from the queue
    if (notifications.length > 0 && !currentNotification) {
      // We were previously displaying *another* message,
      // so we will buffer them a little bit to give the previous one
      // a few ms to disappear before displaying the next one
      if (prevState.currentNotificationIsOpen && !currentNotification) {
        await delayToBuffer(250);
      }

      this.setState({
        currentNotification: notifications[0],
        currentNotificationIsOpen: true,
      });
    }
  }

  render() {
    const {
      classes,
      intl: { formatMessage },
    } = this.props;

    const { currentNotification, currentNotificationIsOpen } = this.state;

    const options = _.get(
      currentNotification,
      'options',
      {} as Notification['options'],
    );
    const message = _.get(currentNotification, 'message', '');
    const hasMultipleMessages = this.props.notifications.length > 1;

    return (
      <Snackbar
        className={classes.snackbarOverrides}
        isOpen={currentNotificationIsOpen}
        duration={options.duration || 6000}
        statusType={options.statusType || 'info'}
        onClose={this.handleSnackbarClose}
      >
        <Text className={classes.title}>{message}</Text>
        <Divider direction="row" className={classes.divider} />
        {hasMultipleMessages && (
          <Tooltip
            title={formatMessage(messages.dismissAll)}
            placement="bottom"
          >
            <IconButton
              aria-label={formatMessage(messages.dismissAll)}
              onClick={this.handlePurgeNotifications}
              icon={ClearList}
              size={0}
              paddingTop={0}
              paddingBottom={0}
              paddingRight={0}
            />
          </Tooltip>
        )}
        <Tooltip title={formatMessage(messages.dismiss)} placement="bottom">
          <IconButton
            aria-label={formatMessage(messages.dismiss)}
            onClick={this.handleSnackbarClose}
            icon={X}
            size={0}
            paddingTop={0}
            paddingBottom={0}
            paddingRight={0}
          />
        </Tooltip>
      </Snackbar>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  notifications: notificationsSelectors.selectNotifications(state),
});

const mapDispatchToProps = {
  removeNotification: notificationsActions.removeNotification,
  purgeNotifications: notificationsActions.purgeNotifications,
};

export default withStyles(styles)(
  connect(mapStateToProps, mapDispatchToProps)(injectIntl(AppNotifications)),
);
